import { type ReactElement, type ReactNode, useMemo, useRef } from 'react'
import { isDefined, isEmpty, isNotEmpty, type UniversalSearchItem, type UserSearchItem } from '@terros/common'
import { useNavigate } from '@tanstack/react-router'
import { Spotlight, type SpotlightStore } from '@mantine/spotlight'
import { Avatar, Group, Kbd, Loader, Text } from '@mantine/core'
import { type WebSearchScope, useIsMac } from '@hooks'
import { useImpersonate, useProfile, useTranslation } from '@front/model'
import { MaterialSymbolIcon } from '../terros/icon/MaterialSymbolIcon'
import { TButton } from '../buttons/button'
import { detectIdAndGetUrl, getNavigationUrl } from './util'
import { useSearchDestinations } from './useSearchDestinations'
import { useRecentSearchResults } from './useRecentResults'
import { matchSpotlightDestinations } from './spotlightDestinations'
import classes from './spotlight.module.css'
import { SearchResultDetails } from './SearchResultDetails'
import { SearchResultAvatar } from './SearchResultAvatar'
import { DetectedIdItem } from './DetectedIdItem'
import { AccountSearchResultContents } from './AccountSearchResultContents'

export const UniversalSearchContent = ({
  scope,
  results,
  isLoading,
  error,
  searchValue,
  close,
  store,
}: {
  scope: WebSearchScope
  results: UniversalSearchItem[]
  isLoading: boolean
  error: boolean
  searchValue: string
  close: VoidFunction
  store: SpotlightStore
}): ReactElement => {
  const { t } = useTranslation()
  const pointerPosition = useRef<{ x: number; y: number } | undefined>(undefined)
  const navigate = useNavigate()
  const { isTantalim, user, hasPermission, isLoading: profileLoading, error: profileError } = useProfile()
  const { recentResults, addResultItem, clearRecentResults } = useRecentSearchResults(scope)
  const destinations = useSearchDestinations()
  const matches = useMemo(() => {
    if (scope !== 'all' && scope !== 'destinations') return []
    return matchSpotlightDestinations(destinations, searchValue)
  }, [searchValue, scope, destinations])
  const canViewFeatures = isTantalim && isDefined(user) && !profileLoading && !profileError
  const detectedId = scope === 'destinations' ? undefined : detectIdAndGetUrl(searchValue, canViewFeatures)
  const showingRecent = isEmpty(searchValue.trim())
  const searchItems = scope === 'destinations' ? [] : results
  const items = showingRecent ? recentResults : searchItems
  const navigableItems = items.filter(
    (item) =>
      getNavigationUrl(item) && (item.type !== 'Destination' || destinations.some((route) => route.path === item.path))
  )
  const isExternalDestination = (path: string): boolean =>
    destinations.some((route) => route.isExternalLink && route.path === path)
  const openDestination = (path: string, isExternalLink = false): void => {
    if (isExternalLink) {
      window.open(path, '_blank', 'noopener,noreferrer')
      return
    }
    const destination = new URL(path, window.location.origin)
    navigate({ to: destination.pathname, search: Object.fromEntries(destination.searchParams) })
  }

  return (
    <>
      <Spotlight.ActionsList
        aria-busy={isLoading}
        // Keep the sibling impersonate button's row selected for Cmd+I. This uses Mantine-owned
        // data-selected/store state; recheck pointer and keyboard selection when upgrading Spotlight.
        onMouseMove={(event) => {
          // Scrolling the keyboard selection under a stationary pointer can also emit mousemove.
          if (pointerPosition.current?.x === event.clientX && pointerPosition.current?.y === event.clientY) return
          pointerPosition.current = { x: event.clientX, y: event.clientY }
          if (!(event.target instanceof Element)) return
          const target = event.target
          const action =
            target.closest('[data-search-result]')?.querySelector('[data-action]') ?? target.closest('[data-action]')
          if (!action) return
          const index = Array.from(event.currentTarget.querySelectorAll('[data-action]')).indexOf(action)
          if (index >= 0 && index !== store.getState().selected) {
            event.currentTarget.querySelector('[data-selected]')?.removeAttribute('data-selected')
            action.setAttribute('data-selected', 'true')
            store.updateState((state) => ({ ...state, selected: index }))
          }
        }}
      >
        {isNotEmpty(matches) && (
          <Spotlight.ActionsGroup label={t('App destinations')}>
            {matches.map((route) => (
              <Spotlight.Action
                key={route.path}
                label={route.label}
                description={route.section ? `${route.section} › ${route.label}` : route.path}
                leftSection={
                  <Avatar size={36} radius='xl'>
                    <MaterialSymbolIcon icon={route.icon ?? 'search'} size='xl' />
                  </Avatar>
                }
                highlightQuery
                onClick={() => {
                  addResultItem({
                    type: 'Destination',
                    path: route.path,
                    description: route.label,
                    subtitle: route.section ? `${route.section} › ${route.label}` : route.path,
                    icon: route.icon,
                  })
                  openDestination(route.path, route.isExternalLink)
                }}
              />
            ))}
          </Spotlight.ActionsGroup>
        )}
        {detectedId && (
          <Spotlight.ActionsGroup label={t('Pasted ID or email')}>
            <DetectedIdItem key={detectedId.id} detected={detectedId} onNavigate={openDestination} />
          </Spotlight.ActionsGroup>
        )}
        {isLoading && (
          <Group p='md' role='status'>
            <Loader size='xs' />
            <Text size='sm'>{t('Searching entities…')}</Text>
          </Group>
        )}
        {error && (
          <Text p='md' c='red' role='alert'>
            {t('Entity search failed. Try again.')}
          </Text>
        )}
        {isNotEmpty(navigableItems) && (
          <Spotlight.ActionsGroup label={t(showingRecent ? 'Recent' : 'Results')}>
            {navigableItems.map((item) => {
              const url = getNavigationUrl(item)!
              const onSelect = (): void => {
                addResultItem(item)
                const external = item.type === 'Destination' && isExternalDestination(url)
                openDestination(url, external)
              }
              const canImpersonate =
                item.type === 'User' &&
                user &&
                user.userId !== item.userId &&
                (hasPermission('user:login') || isTantalim)
              return (
                <div key={url} className={classes.resultRow} data-search-result>
                  <Spotlight.Action onClick={onSelect}>
                    {item.type === 'Account' ? (
                      <AccountSearchResultContents item={item} />
                    ) : (
                      <>
                        <SearchResultAvatar item={item} />
                        <SearchResultDetails item={item} />
                      </>
                    )}
                  </Spotlight.Action>
                  {canImpersonate && (
                    <ImpersonateAction
                      item={item}
                      onSelect={() => {
                        addResultItem(item)
                        close()
                      }}
                    />
                  )}
                </div>
              )
            })}
            {showingRecent && (
              <Spotlight.Action
                label={t('Clear recent results')}
                closeSpotlightOnTrigger={false}
                onClick={clearRecentResults}
              />
            )}
          </Spotlight.ActionsGroup>
        )}
        {!isLoading && !error && isEmpty(matches) && !detectedId && isEmpty(navigableItems) && (
          <Spotlight.Empty>{t(searchValue.trim() ? 'No matches found' : 'Start typing to search')}</Spotlight.Empty>
        )}
        <SearchKeyboardHints />
      </Spotlight.ActionsList>
    </>
  )
}

const SearchKeyboardHints = (): ReactElement => {
  const { t } = useTranslation()
  const isMac = useIsMac()
  return (
    <Group
      className={classes.footer}
      gap='sm'
      px='md'
      py='xs'
      fz='sm'
      c='dimmed'
      aria-label={t('Search keyboard shortcuts')}
      role='group'
    >
      <span className='inline-flex items-center gap-1'>
        <Kbd size='sm' aria-label={t('Up arrow')}>
          <MaterialSymbolIcon icon='arrow_upward' size='sm' className='align-middle' />
        </Kbd>
        <Kbd size='sm' aria-label={t('Down arrow')}>
          <MaterialSymbolIcon icon='arrow_downward' size='sm' className='align-middle' />
        </Kbd>
        <span>{t('Navigate')}</span>
      </span>
      <span className='inline-flex items-center gap-1'>
        <Kbd size='sm'>Enter</Kbd>
        <span>{t('Open')}</span>
      </span>
      <span className='inline-flex items-center gap-1'>
        <Kbd size='sm'>Esc</Kbd>
        <span>{t('Close')}</span>
      </span>
      <span className={`${classes.impersonateHint} items-center gap-1`}>
        <Kbd size='sm'>{isMac ? '⌘' : 'Ctrl'}</Kbd>
        <Kbd size='sm'>I</Kbd>
        <span>{t('Impersonate')}</span>
      </span>
    </Group>
  )
}

const ImpersonateAction = ({ item, onSelect }: { item: UserSearchItem; onSelect: VoidFunction }): ReactNode => {
  const { start, allowed } = useImpersonate(item.userId)
  const { t } = useTranslation()
  const isMac = useIsMac()
  if (!allowed) return null
  return (
    <TButton
      variant='secondary'
      size='xs'
      className='shrink-0'
      mr='xs'
      data-impersonate
      aria-label={t('Impersonate {{name}}', { name: item.description })}
      aria-keyshortcuts={isMac ? 'Meta+i' : 'Control+i'}
      onClick={() => {
        onSelect()
        start()
      }}
    >
      {t('Impersonate')}
    </TButton>
  )
}
